import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { SegmentedLinks } from '@/shared/ui/SegmentedLinks';
import {
  ScreenTabsContext,
  type ScreenTab,
  type ScreenTabsValue,
} from '@/shared/lib/screenTabsContext';

// Провайдер вешается в роутере на каждый экран группы (см. buildRoutes).
// Заголовок раздела и таб-бар рисует PageHeader.
export const ScreenTabsProvider = ({
  value,
  children,
}: {
  value: ScreenTabsValue;
  children: ReactNode;
}) => <ScreenTabsContext.Provider value={value}>{children}</ScreenTabsContext.Provider>;

// Активный таб определяется по текущему пути, а не пропсом: обёртке незачем
// знать, какой экран она сейчас оборачивает.
//
// Вид: 'underline' — подписи с чертой под активной (табы раздела, tabGroups);
// 'segmented' — белая пилюля с сегментами, активный залит тёмным (табы-сегменты
// ниже lg по макету telecom, NavGroup.compactTabs).
export const ScreenTabBar = ({
  tabs,
  className,
  variant = 'underline',
}: {
  tabs: ScreenTab[];
  className?: string;
  variant?: 'underline' | 'segmented';
}) => {
  const { pathname } = useLocation();

  if (variant === 'segmented') {
    return (
      <SegmentedLinks
        className={className}
        items={tabs.map((tab) => ({
          key: tab.id,
          to: tab.path,
          label: tab.label,
          active: pathname === tab.path,
        }))}
      />
    );
  }

  return (
    <nav className={cn('flex items-center gap-4 lg:gap-6', className)}>
      {tabs.map((tab) => {
        const isActive = pathname === tab.path;

        return (
          <Link
            key={tab.id}
            to={tab.path}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'whitespace-nowrap border-b-2 pb-1 text-base font-bold leading-6 transition-colors lg:text-lg',
              isActive
                ? 'border-line-accent text-content-accent'
                : 'border-transparent text-content-secondary hover:text-content-accent',
            )}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
};
